import React, { useEffect, useMemo, useState, useCallback } from "react";
import useAutoRefresh from "@/lib/useAutoRefresh";
import {
  Bell,
  X,
  WarningOctagon,
  Warning,
  Receipt,
  ClockCounterClockwise,
  CaretLeft,
  CaretRight,
  CheckCircle,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { fetchNotificationsToday } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_MS = 30000;

const TYPE_META = {
  impact: {
    label: "Case",
    icon: WarningOctagon,
    cls: "border-red-500 bg-red-50",
    dot: "bg-red-600",
    pill: "border-red-500 bg-red-50 text-red-800",
    pillActive: "border-red-600 bg-red-600 text-white",
  },
  sanction: {
    label: "Sanction",
    icon: Warning,
    cls: "border-amber-500 bg-amber-50",
    dot: "bg-amber-500",
    pill: "border-amber-500 bg-amber-50 text-amber-800",
    pillActive: "border-amber-600 bg-amber-500 text-white",
  },
  fine: {
    label: "Amende",
    icon: Receipt,
    cls: "border-fuchsia-500 bg-fuchsia-50",
    dot: "bg-fuchsia-600",
    pill: "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800",
    pillActive: "border-fuchsia-600 bg-fuchsia-600 text-white",
  },
};

function shortTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (sameDay) {
      return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
    // Older event → show short date + time
    return (
      d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) +
      " " +
      d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return "";
  }
}

function photoUrl(key) {
  if (!key) return "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/files/${encodeURIComponent(key)}`;
}

// Per-user localStorage for seen notification IDs
const seenKey = (uid) => `nytrans:notif-seen:${uid || "anon"}`;

function loadSeenSet(uid) {
  try {
    const raw = localStorage.getItem(seenKey(uid));
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveSeenSet(uid, set) {
  try {
    localStorage.setItem(seenKey(uid), JSON.stringify([...set]));
  } catch {
    // quota exceeded etc — silent
  }
}

export default function NotificationsFeed() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [seenIds, setSeenIds] = useState(new Set());
  const [filter, setFilter] = useState("all"); // all|impact|sanction|fine
  const [lightbox, setLightbox] = useState(null); // {photos:[], index:0, title:''}
  const navigate = useNavigate();
  const { user } = useAuth();

  // Load persistent "seen" set from localStorage on user change
  useEffect(() => {
    if (user) setSeenIds(loadSeenSet(user.id));
  }, [user]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await fetchNotificationsToday();
      setData(res);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
  }, [load, user]);
  useAutoRefresh(load, POLL_MS);

  const markAllSeen = useCallback(() => {
    if (!user) return;
    const next = new Set(seenIds);
    for (const it of data.items || []) next.add(`${it.type}:${it.id}`);
    setSeenIds(next);
    saveSeenSet(user.id, next);
  }, [user, seenIds, data.items]);

  const markOneSeen = useCallback(
    (id) => {
      if (!user) return;
      if (seenIds.has(id)) return;
      const next = new Set(seenIds);
      next.add(id);
      setSeenIds(next);
      saveSeenSet(user.id, next);
    },
    [user, seenIds]
  );

  const unseenCount = useMemo(() => {
    return (data.items || []).filter((it) => !seenIds.has(`${it.type}:${it.id}`)).length;
  }, [data.items, seenIds]);

  const unseenByType = useMemo(() => {
    const out = { impact: 0, sanction: 0, fine: 0 };
    for (const it of data.items || []) {
      if (!seenIds.has(`${it.type}:${it.id}`)) {
        if (out[it.type] !== undefined) out[it.type] += 1;
      }
    }
    return out;
  }, [data.items, seenIds]);

  const filteredItems = useMemo(() => {
    if (filter === "all") return data.items || [];
    return (data.items || []).filter((it) => it.type === filter);
  }, [data.items, filter]);

  const totalToday = data?.counts?.total || 0;

  const openLightbox = (photos, index, title) => {
    setLightbox({ photos, index, title });
  };
  const closeLightbox = () => setLightbox(null);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft")
        setLightbox((lb) => (lb ? { ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length } : lb));
      else if (e.key === "ArrowRight")
        setLightbox((lb) => (lb ? { ...lb, index: (lb.index + 1) % lb.photos.length } : lb));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox]);

  if (!user) return null;

  return (
    <>
      {/* Floating toggle button — left */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed left-3 bottom-3 z-40 w-12 h-12 border-2 border-zinc-900 bg-white hover:bg-[var(--ny-gold)] hover:text-white shadow-lg flex items-center justify-center transition-colors"
        title="Notifications du jour"
        data-testid="notif-feed-toggle"
      >
        <Bell size={22} weight="fill" />
        {unseenCount > 0 && (
          <span
            className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            data-testid="notif-feed-badge"
          >
            {unseenCount > 99 ? "99+" : unseenCount}
          </span>
        )}
      </button>

      {/* Panel — left slide */}
      <div
        className={`fixed left-0 top-0 bottom-0 z-50 w-[380px] max-w-[92vw] bg-white border-r-2 border-zinc-900 shadow-2xl flex flex-col transform transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="notif-feed-panel"
      >
        <div className="p-3 border-b-2 border-zinc-900 bg-zinc-900 text-white flex items-center gap-2">
          <Bell size={18} weight="fill" className="text-[var(--ny-gold)]" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest font-bold">Activité récente</div>
            <div className="text-[10px] text-zinc-400 uppercase tracking-widest truncate">
              {data.days
                ? `${data.days} derniers jours · rafraîchi 30s`
                : "30 derniers jours · rafraîchi 30s"}
            </div>
          </div>
          <button
            type="button"
            onClick={markAllSeen}
            disabled={unseenCount === 0}
            className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-white/40 hover:border-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
            title="Marquer toutes les notifications comme lues"
            data-testid="notif-mark-all-read"
          >
            <CheckCircle size={12} weight="bold" />
            Tout lu
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-white hover:text-red-400"
            data-testid="notif-feed-close"
            title="Fermer"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Clickable category pills — filter */}
        <div className="grid grid-cols-4 gap-1 p-2 border-b border-zinc-200 bg-zinc-50">
          <FilterPill
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label="Tout"
            count={data.counts?.total || 0}
            unseen={unseenCount}
            testId="notif-filter-all"
          />
          <FilterPill
            active={filter === "impact"}
            onClick={() => setFilter("impact")}
            label="Cases"
            count={data.counts?.impacts || 0}
            unseen={unseenByType.impact}
            tone="red"
            testId="notif-filter-impact"
          />
          <FilterPill
            active={filter === "sanction"}
            onClick={() => setFilter("sanction")}
            label="Sanct."
            count={data.counts?.sanctions || 0}
            unseen={unseenByType.sanction}
            tone="amber"
            testId="notif-filter-sanction"
          />
          <FilterPill
            active={filter === "fine"}
            onClick={() => setFilter("fine")}
            label="Amendes"
            count={data.counts?.fines || 0}
            unseen={unseenByType.fine}
            tone="fuchsia"
            testId="notif-filter-fine"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (data.items || []).length === 0 && (
            <div className="p-6 text-center text-xs text-zinc-500 uppercase tracking-widest">
              Chargement…
            </div>
          )}
          {!loading && totalToday === 0 && (
            <div className="p-8 text-center text-zinc-500">
              <ClockCounterClockwise size={28} weight="duotone" className="mx-auto mb-2" />
              <div className="text-sm font-semibold">Aucun événement récent</div>
              <div className="text-xs mt-1">Rien à signaler sur les 30 derniers jours.</div>
            </div>
          )}
          {!loading && totalToday > 0 && filteredItems.length === 0 && (
            <div className="p-8 text-center text-zinc-500">
              <div className="text-sm">Rien à afficher dans cette catégorie.</div>
            </div>
          )}
          {filteredItems.map((it) => (
            <NotifItem
              key={`${it.type}:${it.id}`}
              item={it}
              unread={!seenIds.has(`${it.type}:${it.id}`)}
              onOpen={() => markOneSeen(`${it.type}:${it.id}`)}
              onNavigate={(url) => {
                setOpen(false);
                navigate(url);
              }}
              onOpenPhotos={(photos, i, title) => openLightbox(photos, i, title)}
            />
          ))}
        </div>
      </div>

      {/* Lightbox (photo carousel) */}
      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          index={lightbox.index}
          title={lightbox.title}
          onClose={closeLightbox}
          onPrev={() =>
            setLightbox((lb) => ({
              ...lb,
              index: (lb.index - 1 + lb.photos.length) % lb.photos.length,
            }))
          }
          onNext={() =>
            setLightbox((lb) => ({
              ...lb,
              index: (lb.index + 1) % lb.photos.length,
            }))
          }
          onSelect={(i) =>
            setLightbox((lb) => ({
              ...lb,
              index: i,
            }))
          }
        />
      )}
    </>
  );
}

function FilterPill({ active, onClick, label, count, unseen, tone, testId }) {
  const meta = tone ? TYPE_META[tone === "red" ? "impact" : tone === "amber" ? "sanction" : "fine"] : null;
  const cls = active
    ? meta
      ? meta.pillActive
      : "border-zinc-900 bg-zinc-900 text-white"
    : meta
    ? meta.pill + " hover:brightness-95"
    : "border-zinc-300 bg-white text-zinc-900 hover:border-zinc-900";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border ${cls} text-center py-1 px-1 transition-colors relative`}
      data-testid={testId}
    >
      <div className="text-[9px] uppercase tracking-widest font-bold">{label}</div>
      <div className="text-base font-bold mono leading-tight">{count}</div>
      {unseen > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center border border-white">
          {unseen > 9 ? "9+" : unseen}
        </span>
      )}
    </button>
  );
}

function NotifItem({ item, unread, onOpen, onNavigate, onOpenPhotos }) {
  const meta = TYPE_META[item.type] || TYPE_META.impact;
  const Icon = meta.icon;
  const photos = item.photos || (item.type === "fine" && item.photo ? [item.photo] : []);
  const title = `${meta.label} · ${item.truck_plate || ""}`.trim();

  return (
    <div
      className={`border-l-4 ${meta.cls.split(" ")[0]} border-b border-zinc-200 p-3 hover:bg-zinc-50 cursor-pointer relative ${
        unread ? "" : "opacity-70"
      }`}
      onClick={() => {
        onOpen();
        if (item.truck_id) onNavigate(`/trucks/${item.truck_id}`);
      }}
      data-testid={`notif-item-${item.type}-${item.id}`}
    >
      {unread && (
        <span className="absolute right-2 top-2 w-2 h-2 rounded-full bg-red-600" title="Non lu" />
      )}
      <div className="flex items-start gap-2">
        <div className={`shrink-0 w-6 h-6 flex items-center justify-center text-white ${meta.dot}`}>
          <Icon size={12} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-900">
              {meta.label}
            </span>
            {item.severity && (
              <span
                className={`px-1 py-0.5 text-[9px] font-bold uppercase ${
                  item.severity === "majeur" ? "bg-red-600 text-white" : "bg-amber-500 text-white"
                }`}
              >
                {item.severity}
              </span>
            )}
            <span className="ml-auto text-[10px] text-zinc-400 mono">{shortTime(item.created_at)}</span>
          </div>

          {item.truck_plate && (
            <div className="text-sm font-bold mono uppercase">
              {item.truck_plate}
              {item.truck_brand && (
                <span className="ml-1 text-xs text-zinc-500 normal-case">
                  · {item.truck_brand}
                </span>
              )}
              {item.site && (
                <span className="ml-1 text-[10px] text-zinc-400 normal-case">
                  · {item.site}
                </span>
              )}
            </div>
          )}

          {item.driver && (
            <div className="text-xs text-zinc-700 truncate">👤 {item.driver}</div>
          )}

          {item.type === "impact" && item.note && (
            <div className="text-xs text-zinc-500 italic truncate">{item.note}</div>
          )}

          {item.type === "sanction" && (
            <div className="text-xs text-zinc-700">
              {item.months} mois · {item.monthly_amount?.toFixed(2)} €/mois
              {item.reason && <span className="text-zinc-500"> · {item.reason}</span>}
            </div>
          )}

          {item.type === "fine" && (
            <div className="text-xs text-zinc-700">
              {(item.amount || 0).toFixed(2)} €
              {item.reason && <span className="text-zinc-500"> · {item.reason}</span>}
            </div>
          )}

          {photos.length > 0 && (
            <div className="flex gap-1 mt-1.5 flex-wrap">
              {photos.slice(0, 4).map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen();
                    onOpenPhotos(photos, i, title);
                  }}
                  className="block w-12 h-12 border border-zinc-300 overflow-hidden bg-zinc-100 hover:border-zinc-900"
                  title={`Voir la photo ${i + 1}/${photos.length}`}
                  data-testid={`notif-photo-${item.type}-${item.id}-${i}`}
                >
                  <img
                    src={photoUrl(p)}
                    alt=""
                    className="w-full h-full object-cover pointer-events-none"
                    loading="lazy"
                  />
                </button>
              ))}
              {photos.length > 4 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen();
                    onOpenPhotos(photos, 4, title);
                  }}
                  className="w-12 h-12 border border-zinc-300 bg-zinc-50 hover:border-zinc-900 flex items-center justify-center text-[10px] font-bold text-zinc-500"
                >
                  +{photos.length - 4}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Lightbox({ photos, index, title, onClose, onPrev, onNext, onSelect }) {
  const src = photoUrl(photos[index]);
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="notif-lightbox"
    >
      <div
        className="relative max-w-[95vw] max-h-[90vh] w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center"
          data-testid="notif-lightbox-close"
          title="Fermer (Esc)"
        >
          <X size={22} weight="bold" />
        </button>

        {/* Header */}
        <div className="absolute top-2 left-2 z-10 text-white text-xs uppercase tracking-widest font-bold bg-black/60 px-2 py-1">
          {title} · {index + 1}/{photos.length}
        </div>

        {/* Image */}
        <img
          src={src}
          alt=""
          className="max-w-full max-h-full object-contain select-none"
          draggable={false}
        />

        {/* Prev / Next arrows */}
        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={onPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 text-white hover:bg-black/80 flex items-center justify-center"
              data-testid="notif-lightbox-prev"
              title="Précédente (←)"
            >
              <CaretLeft size={22} weight="bold" />
            </button>
            <button
              type="button"
              onClick={onNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 text-white hover:bg-black/80 flex items-center justify-center"
              data-testid="notif-lightbox-next"
              title="Suivante (→)"
            >
              <CaretRight size={22} weight="bold" />
            </button>
          </>
        )}

        {/* Thumbnail strip */}
        {photos.length > 1 && (
          <div
            className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 max-w-full px-2 overflow-x-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {photos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onSelect(i)}
                className={`w-14 h-14 border-2 overflow-hidden shrink-0 ${
                  i === index ? "border-[var(--ny-gold)]" : "border-white/40 hover:border-white"
                }`}
                data-testid={`notif-lightbox-thumb-${i}`}
              >
                <img
                  src={photoUrl(p)}
                  alt=""
                  className="w-full h-full object-cover pointer-events-none"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
